import * as TSL from "three/tsl";
import { registerSurface, type SurfaceInputs } from "./ShaderSurface.js";

/**
 * The built-in surface presets. Five pictures - aurora, nebula, heat shimmer, a drifting gradient
 * mesh and water - and two that work on a picture they are handed: a burn and a blur.
 *
 * Each reads `colorA`, `colorB` and `colorC` (C is the ground), `params.x` as intensity and
 * `params.y` as scale; what `params.z` and `params.w` mean is the preset's own, and said beside it.
 * The pictures are drawn at half resolution: they are soft by nature, and a phone drawing a full-
 * screen one at full size is spending its battery on detail nobody can see.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;
const {
  abs, clamp, cos, distance, exp, float, floor, length, max, mix, pow, select, sin, smoothstep, step,
  vec2, vec3, vec4,
} = TSL as N;

/** Scale, never below a tenth. */
const scaleOf = (s: SurfaceInputs): N => max(s.params.y, 0.1);
/** Width over height. */
const aspectOf = (s: SurfaceInputs): N => s.size.x.div(max(s.size.y, 1.0));

registerSurface("aurora", {
  animated: true,
  resolution: 0.5,
  fragment: (s) => {
    const sc = scaleOf(s);
    const p = vec2(s.uv.x.mul(aspectOf(s)), s.uv.y).div(sc);
    const t = s.time.mul(0.12);
    let tint: N = vec3(0.0);
    for (let i = 0; i < 3; i++) {
      const warp = s.fbm(vec2(p.x.mul(1.5).add(i * 3.1), t)).mul(3.0);
      const wave = sin(p.x.mul(1.4 + i * 0.6).add(t.mul(1.0 + i * 0.3)).add(warp)).mul(0.12);
      const center = wave.add(0.28 + i * 0.14);
      const d = s.uv.y.sub(center).div(0.06 + 0.03 * i);
      const band = exp(d.mul(d).negate());
      // Curtains: fine vertical streaks that shimmer along the band.
      const streak = s.noise(vec2(p.x.mul(18.0).add(i * 7.0), t.mul(3.0))).mul(0.45).add(0.55);
      tint = tint.add(mix(s.colorA.rgb, s.colorB.rgb, i * 0.5).mul(band).mul(streak));
    }
    const ground = s.colorC.rgb.mul(float(1.0).sub(s.uv.y).mul(0.45).add(0.55));
    return vec4(ground.add(tint.mul(s.params.x)), 1.0);
  },
});

registerSurface("nebula", {
  animated: true,
  resolution: 0.5,
  fragment: (s) => {
    const p = vec2(s.uv.x.mul(aspectOf(s)), s.uv.y).mul(2.5).div(scaleOf(s));
    const t = s.time;
    const q = vec2(s.fbm(p.add(vec2(t.mul(0.03), 0.0))), s.fbm(p.add(vec2(5.2, 1.3)).sub(vec2(0.0, t.mul(0.025)))));
    const n = s.fbm(p.add(q.mul(2.2)));
    const cloud = smoothstep(0.3, 0.95, n).mul(s.params.x);
    const rgb = mix(s.colorC.rgb, mix(s.colorA.rgb, s.colorB.rgb, clamp(q.x.mul(1.4).sub(0.2), 0.0, 1.0)), cloud);
    // Stars: one cell in a few hundred lit, each breathing at its own rate.
    const cell = floor(s.uv.mul(s.size).div(3.0));
    const h = s.hash(cell);
    const star = step(0.9965, h).mul(sin(t.mul(h.mul(3.0).add(1.0)).add(h.mul(60.0))).mul(0.45).add(0.55));
    return vec4(rgb.add(vec3(star).mul(float(1.0).sub(cloud.mul(0.6)))), 1.0);
  },
});

// params.z: how high the heat reaches, 0..1 (0.5 when unset reads as 0).
registerSurface("shimmer", {
  animated: true,
  resolution: 0.5,
  fragment: (s) => {
    const sc = scaleOf(s);
    const t = s.time;
    const wob = s.fbm(vec2(s.uv.x.mul(4.0).div(sc), s.uv.y.mul(3.0).div(sc).add(t.mul(0.8))));
    const y = s.uv.y.add(wob.sub(0.5).mul(0.09).mul(s.params.x));
    const reach = mix(0.5, 0.05, clamp(s.params.z, 0.0, 1.0));
    const heat = smoothstep(reach, 1.0, y);
    const lick = sin(t.mul(3.0).add(s.uv.x.mul(22.0)).add(wob.mul(6.0))).mul(0.2).add(0.8);
    const rgb = mix(s.colorC.rgb, s.colorA.rgb, heat).add(s.colorB.rgb.mul(pow(heat, 4.0)).mul(0.6).mul(lick));
    return vec4(rgb, 1.0);
  },
});

registerSurface("mesh", {
  animated: true,
  resolution: 0.5,
  fragment: (s) => {
    const t = s.time.mul(0.15);
    const reach = scaleOf(s).mul(0.42);
    const blob = (center: N): N => {
      const d = distance(s.uv, center);
      return exp(d.mul(d).negate().div(reach.mul(reach)));
    };
    const a = blob(vec2(sin(t).mul(0.2).add(0.25), cos(t.mul(1.3)).mul(0.2).add(0.3)));
    const b = blob(vec2(cos(t.mul(0.9)).mul(0.2).add(0.75), sin(t.mul(1.1)).mul(0.25).add(0.35)));
    const c = blob(vec2(sin(t.mul(0.7).add(2.0)).mul(0.3).add(0.5), cos(t.mul(1.6)).mul(0.15).add(0.8)));
    const total = a.add(b).add(c).add(0.35);
    const mixA = s.colorA.rgb.mul(a).add(s.colorB.rgb.mul(b)).add(mix(s.colorA.rgb, s.colorB.rgb, 0.5).mul(c));
    const rgb = mix(s.colorC.rgb, mixA.div(max(a.add(b).add(c), 1.0e-3)), clamp(total.sub(0.35).mul(s.params.x), 0.0, 1.0));
    return vec4(rgb, 1.0);
  },
});

registerSurface("water", {
  animated: true,
  resolution: 0.5,
  fragment: (s) => {
    const p = vec2(s.uv.x.mul(aspectOf(s)), s.uv.y).mul(3.0).div(scaleOf(s));
    const t = s.time.mul(0.35);
    // Caustics as the lines where two drifting noise fields agree: a web of bright threads that
    // re-forms as the fields slide past each other, which is what light through a moving surface does.
    const n1 = s.fbm(p.add(vec2(t, t.mul(-0.6))));
    const n2 = s.fbm(p.mul(1.35).sub(vec2(t.mul(0.7), t.mul(0.4))).add(vec2(3.1, 7.7)));
    const ridge = clamp(float(1.0).sub(abs(n1.sub(n2)).mul(7.0)), 0.0, 1.0);
    const light = pow(ridge, 5.0).mul(s.params.x);
    const depth = mix(s.colorA.rgb, s.colorC.rgb, smoothstep(0.0, 1.0, s.uv.y));
    return vec4(depth.mul(n1.mul(0.25).add(0.75)).add(s.colorB.rgb.mul(light)), 1.0);
  },
});

// params.z: progress, 0 (whole) to 1 (gone). params.w: the width of the burning edge, 0 for the
// default. point.xy: where the fire catches, 0..1 - the burn spreads out from there.
//
// What changes from one burn to the next is the caller's to vary: a different seed gives a
// different coastline, a different scale a coarser or finer one, and the point moves where it
// starts. The edge flickers and throws embers on the surface's own clock, so it wants `animate`.
registerSurface("burn", {
  animated: true,
  usesInput: true,
  fragment: (s) => {
    const src = s.input(s.uv);
    const sc = scaleOf(s);
    const aspect = aspectOf(s);
    const q = vec2(s.uv.x.mul(aspect), s.uv.y);
    const t = s.time;

    // Distance from where the fire caught, as a fraction of the corner farthest from it, so the
    // burn finishes at the same progress wherever it started.
    const o = vec2(s.point.x.mul(aspect), s.point.y);
    const reach = max(
      max(length(o), length(o.sub(vec2(aspect, 0.0)))),
      max(length(o.sub(vec2(0.0, 1.0))), length(o.sub(vec2(aspect, 1.0)))),
    );
    const d = length(q.sub(o)).div(max(reach, 1.0e-3));

    // A coastline rather than a circle: the distance broken up by noise, and licking a little as
    // it goes so the front is alive while it moves.
    const n = s.fbm(q.mul(2.2).div(sc));
    const lick = s.noise(q.mul(9.0).div(sc).add(vec2(0.0, t.mul(-2.5)))).sub(0.5);
    const field = d.mul(0.72).add(n.mul(0.38)).add(lick.mul(0.06));
    const cut = field.sub(s.params.z.mul(1.3).sub(0.14));
    const edgeWidth = select(s.params.w.greaterThan(0.0), s.params.w, float(0.05));

    // Burnt through. Just behind the front, embers: specks drifting up and going out.
    const behind = cut.negate();
    const cell = floor(s.uv.add(vec2(0.0, t.mul(0.12))).mul(s.size).div(3.0));
    const h = s.hash(cell);
    const spark = step(0.982, h)
      .mul(float(1.0).sub(smoothstep(0.0, 0.14, behind)))
      .mul(sin(t.mul(9.0).add(h.mul(40.0))).mul(0.5).add(0.5));
    const ember = vec4(s.colorB.rgb, clamp(spark, 0.0, 1.0));

    const edge = float(1.0).sub(smoothstep(0.0, edgeWidth, cut));
    const heat = float(1.0).sub(smoothstep(0.0, edgeWidth.mul(3.5), cut));
    const char = smoothstep(edgeWidth, edgeWidth.mul(2.2), cut);
    const flicker = s.noise(q.mul(14.0).add(vec2(t.mul(3.0), t.mul(-4.0)))).mul(0.4).add(0.8);
    const fire = mix(s.colorA.rgb, s.colorB.rgb, edge.mul(edge)).mul(flicker);
    // The page scorching ahead of the flames, before it chars.
    const scorched = src.rgb.mul(mix(0.15, 1.0, char)).add(s.colorA.rgb.mul(heat).mul(0.3).mul(flicker));
    const page = vec4(mix(scorched, fire.mul(s.params.x.add(1.0)), edge), max(src.a, edge));

    return select(cut.lessThanEqual(0.0), ember, page);
  },
});

// params.x: how far the blur reaches. params.w: how much the picture is dimmed, 0..1.
registerSurface("blur", {
  animated: false,
  usesInput: true,
  resolution: 0.5,
  fragment: (s) => {
    const texel = vec2(1.0).div(max(s.size, vec2(1.0)));
    const reach = max(s.params.x, 0.0).mul(10.0);
    let total: N = vec4(0.0);
    let weight: N = float(0.0);
    // A golden-angle spiral: 32 taps spread evenly over a disc, no grid to show through.
    for (let i = 0; i < 32; i++) {
      const fi = i + 0.5;
      const r = reach.mul(Math.sqrt(fi / 32));
      const a = fi * 2.39996;
      const w = float(1.0).sub(r.div(max(reach.mul(1.1), 1.0e-3)));
      total = total.add(s.input(s.uv.add(vec2(Math.cos(a), Math.sin(a)).mul(r).mul(texel))).mul(w));
      weight = weight.add(w);
    }
    const c = total.div(max(weight, 1.0e-3));
    return vec4(c.rgb.mul(float(1.0).sub(clamp(s.params.w, 0.0, 1.0))), c.a);
  },
});

